/**
 * Waiver requests — sending a waiver to chosen families, signing by link,
 * and the families still missing one.
 *
 * Failure shape mirrors success shape: every function here resolves the
 * response BODY on success, so on failure it REJECTS with the axios error
 * (the backend's own `{ error }` body stays on `err.response.data`). Callers
 * read the message with `waiverErrorMessage(err)`.
 */
import axios, { publicAxios } from "./axiosInstance";
import config from "./config";
import { getTenantId } from "./apiHelpers";

const BASE_URL = config.BASE_URL;

export interface WaiverBlock {
  id: string;
  type: "heading" | "text" | "form_field";
  content?: string;
  field_type?: "text" | "checkbox" | "signature" | "date";
  field_label?: string;
  required?: boolean;
}

/** A waiver a family is being asked to sign. */
export interface PendingWaiver {
  waiver_id: string;
  name: string;
  description: string | null;
  version: number;
  /**
   * The kept wording row this page is showing. It goes back with the
   * signature; the server refuses it once the wording has moved on, so a
   * family never signs text it did not read.
   */
  version_id?: string | null;
  blocks: WaiverBlock[];
  /** "requested" = staff or the yearly check asked; "out_of_date" = the wording changed since they signed. */
  reason: "requested" | "out_of_date";
  /** The club lets families answer "no" to this one. */
  allow_decline?: boolean;
}

export interface PublicWaiverView {
  club_name: string | null;
  waivers: PendingWaiver[];
  athlete_first_names: string[];
}

export interface SignAnswers {
  signer_name: string;
  agreed: boolean;
  /** "No". Only accepted for a waiver with `allow_decline`. */
  declined?: boolean;
  fields: Record<string, string | boolean>;
}

export interface WaiverSendSummary {
  batch_id: string;
  waiver_id: string;
  requested: number;
  sent: number;
  already_signed: number;
  not_sent: { membership_id: string; reason: string }[];
}

export interface WaiverSendGroup {
  key: string;
  /** null for the yearly check, which is rolled up across families. */
  batch_id: string | null;
  waiver_id: string;
  waiver_name: string;
  label: string | null;
  source: "staff_send" | "annual";
  created_at: string;
  last_sent_at: string | null;
  total: number;
  signed: number;
  pending: number;
}

export interface WaiverSendRow {
  id: string;
  membership_id: string;
  family_name: string;
  email: string | null;
  status: "pending" | "signed" | "cancelled";
  sent_at: string | null;
  last_sent_at: string | null;
  send_count: number;
  last_send_error: string | null;
  signed_at: string | null;
  signed_by_name: string | null;
  signed_version: number | null;
  /** The family answered, and the answer was no. */
  declined?: boolean;
}

export interface MissingWaiverFamily {
  membership_id: string;
  family_name: string;
  email: string | null;
  registration_month: string | null;
  missing: {
    waiver_id: string;
    name: string;
    version: number;
    reason: "never_signed" | "out_of_date";
    signed_version: number | null;
    /** false = the family has no signature and has not been asked for this waiver yet. */
    asked?: boolean;
    asked_because?: string | null;
  }[];
}

export interface MissingWaiversResponse {
  /** Families that owe a waiver: asked, and no current signature. */
  data: MissingWaiverFamily[];
  /** Families with no signature that have not been asked yet. */
  not_asked?: MissingWaiverFamily[];
  required_waivers: { id: string; name: string; version: number }[];
  total_active_families?: number;
}

export interface SignedWaiver {
  id: string;
  waiver_id: string;
  submitted_at: string;
  waiver_version: number | null;
  signed_by_name: string | null;
  signed_by_email?: string | null;
  signed_via: string | null;
  ip_address?: string | null;
  /** `declined: true` here means the family answered no. `fields` are the waiver's own fill-in answers. */
  form_data?: {
    declined?: boolean;
    fields?: Record<string, { label?: string | null; type?: string | null; value?: string | boolean | null }>;
  } | null;
  /** Names of the athletes the signature covers. */
  athlete_names?: string[];
  waivers: { id: string; name: string; description?: string | null; version?: number; blocks?: WaiverBlock[] } | null;
}

const authHeaders = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

/** The backend's own words when it sent any, else a plain fallback. */
export const waiverErrorMessage = (err: any, fallback: string): string =>
  err?.response?.data?.error || fallback;

// ── Emailed link (no sign-in) ──────────────────────────────────────────

export const viewWaiversByLink = async (token: string): Promise<PublicWaiverView> => {
  const response = await publicAxios.post(
    "/waiver-requests/public/view",
    { token },
    { headers: { "x-tenant-id": getTenantId() } }
  );
  return response.data.data as PublicWaiverView;
};

export const signWaiverByLink = async (
  token: string,
  waiverId: string,
  answers: SignAnswers,
  versionId?: string | null
): Promise<void> => {
  await publicAxios.post(
    "/waiver-requests/public/sign",
    { token, waiver_id: waiverId, ...(versionId ? { version_id: versionId } : {}), ...answers },
    { headers: { "x-tenant-id": getTenantId() } }
  );
};

// ── The photo question on a camp, clinic or event registration form ───
// (components/waivers/PhotoReleaseQuestion). null = nothing to ask.

export interface PhotoReleaseQuestionData {
  waiver_id: string;
  name: string;
  description: string | null;
  version: number;
  blocks: WaiverBlock[];
}

/** A signed-in parent: null when their family has already answered. */
export const getMyPhotoReleaseQuestion = async (token: string): Promise<PhotoReleaseQuestionData | null> => {
  const response = await axios.get(`${BASE_URL}/waiver-requests/mine/photo-release`, { headers: authHeaders(token) });
  return (response.data?.data as PhotoReleaseQuestionData | null) || null;
};

/** A public registration page: the club's wording, nothing about anyone. */
export const getPublicPhotoReleaseQuestion = async (): Promise<PhotoReleaseQuestionData | null> => {
  const response = await publicAxios.get("/waiver-requests/public/photo-release", {
    headers: { "x-tenant-id": getTenantId() },
  });
  return (response.data?.data as PhotoReleaseQuestionData | null) || null;
};

// ── Signed-in parent ──────────────────────────────────────────────────

export const getMyPendingWaivers = async (token: string): Promise<PendingWaiver[]> => {
  const response = await axios.get(`${BASE_URL}/waiver-requests/mine`, { headers: authHeaders(token) });
  return (response.data?.data as PendingWaiver[]) || [];
};

export const signMyWaiver = async (
  token: string,
  waiverId: string,
  answers: SignAnswers,
  versionId?: string | null
): Promise<void> => {
  await axios.post(
    `${BASE_URL}/waiver-requests/mine/sign`,
    { waiver_id: waiverId, ...(versionId ? { version_id: versionId } : {}), ...answers },
    { headers: authHeaders(token) }
  );
};

// ── Staff ─────────────────────────────────────────────────────────────

export const sendWaiverToFamilies = async (
  token: string,
  body: { waiver_id: string; membership_ids?: string[]; audienceSpec?: unknown; label?: string }
): Promise<WaiverSendSummary> => {
  const response = await axios.post(`${BASE_URL}/waiver-requests/send`, body, { headers: authHeaders(token) });
  return response.data.data as WaiverSendSummary;
};

export const getWaiverSends = async (token: string, waiverId?: string): Promise<WaiverSendGroup[]> => {
  const response = await axios.get(`${BASE_URL}/waiver-requests/batches`, {
    headers: authHeaders(token),
    params: waiverId ? { waiver_id: waiverId } : {},
  });
  return (response.data?.data as WaiverSendGroup[]) || [];
};

export const getWaiverSend = async (token: string, batchId: string): Promise<WaiverSendRow[]> => {
  const response = await axios.get(`${BASE_URL}/waiver-requests/batches/${batchId}`, { headers: authHeaders(token) });
  return (response.data?.data as WaiverSendRow[]) || [];
};

export const resendWaiverSend = async (
  token: string,
  batchId: string
): Promise<{ resent: number; not_sent: { membership_id: string; reason: string }[] }> => {
  const response = await axios.post(
    `${BASE_URL}/waiver-requests/batches/${batchId}/resend`,
    {},
    { headers: authHeaders(token) }
  );
  return response.data.data;
};

export const getFamiliesMissingWaivers = async (token: string): Promise<MissingWaiversResponse> => {
  const response = await axios.get(`${BASE_URL}/waiver-requests/missing`, { headers: authHeaders(token) });
  return {
    data: response.data?.data || [],
    not_asked: response.data?.not_asked || [],
    required_waivers: response.data?.required_waivers || [],
    total_active_families: response.data?.total_active_families,
  };
};

/** Waivers a family has signed (staff view, on the membership page). */
export const getSignedWaiversForMembership = async (token: string, membershipId: string): Promise<SignedWaiver[]> => {
  const response = await axios.get(`${BASE_URL}/waiver-submissions/membership/${membershipId}`, {
    headers: authHeaders(token),
  });
  return (response.data?.data as SignedWaiver[]) || [];
};
